import { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function base64UrlToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

const supported = () =>
  window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function PushToggle() {
  const [state, setState] = useState("checking"); // checking | unsupported | off | on | busy

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported()) {
        if (!cancelled) setState("unsupported");
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) setState(sub ? "on" : "off");
      } catch {
        if (!cancelled) setState("off");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = async () => {
    setState("busy");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notification permission was not granted. Check your browser settings.");
        setState("off");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToUint8Array(data.public_key),
        });
      }
      await api.post("/push/subscriptions", sub.toJSON());
      setState("on");
      toast.success("Booking notifications enabled on this device.");
    } catch (e) {
      toast.error("Couldn't enable notifications on this device.");
      setState("off");
    }
  };

  const disable = async () => {
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.delete("/push/subscriptions", { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setState("off");
      toast.success("Notifications turned off on this device.");
    } catch {
      setState("on");
      toast.error("Couldn't turn off notifications.");
    }
  };

  if (state === "unsupported") {
    return (
      <span
        data-testid="admin-push-unsupported"
        title="On iPhone: open the site in Safari, Share → Add to Home Screen, then enable notifications from the installed app"
        className="inline-flex items-center gap-1.5 text-xs text-emerald-900/40 px-2 py-2"
      >
        <BellOff size={15} />
        <span className="hidden xl:inline">Push unsupported here</span>
      </span>
    );
  }

  const busy = state === "busy" || state === "checking";
  const on = state === "on";

  return (
    <button
      data-testid="admin-push-toggle-btn"
      onClick={on ? disable : enable}
      disabled={busy}
      title={on ? "Notifications on — tap to turn off on this device" : "Get a push notification on this device for every new booking"}
      className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors duration-200 ${
        on
          ? "bg-limed-light text-pine"
          : "text-emerald-900/60 hover:text-pine hover:bg-sage"
      }`}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : on ? <BellRing size={15} /> : <Bell size={15} />}
      <span className="hidden sm:inline">{on ? "Notifications on" : "Notify me"}</span>
    </button>
  );
}
